import React from "react";
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    PDFDownloadLink,
    Image,
    pdf
} from "@react-pdf/renderer";

const styles = StyleSheet.create({
    page: {
        padding: 20,
        fontSize: 10,
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: "2 solid #000",
        paddingBottom: 10,
        marginBottom: 10,
    },
    headerText: {
        fontSize: 16,
        fontWeight: "bold",
        color: "#333",
    },
    text: {
        fontSize: 10,
        color: "#555",
    },
    tableContainer: {
        marginTop: 10,
        borderWidth: 1,
        borderColor: "#000",
    },
    tableHeader: {
        flexDirection: "row",
        backgroundColor: "#f2f2f2",
        borderBottom: "2 solid #000",
        paddingVertical: 5,
    },
    tableRow: {
        flexDirection: "row",
        borderBottom: "1 solid #ddd",
        paddingVertical: 4,
    },
    tableCell: {
        flex: 1,
        padding: 4,
        textAlign: "center",
    },
    image: {
        width: 60,
        height: 60,
    },
    summary: {
        marginTop: 15,
        padding: 10,
        borderWidth: 1,
        borderColor: "#000",
        backgroundColor: "#f9f9f9",
    },
    summaryText: {
        fontSize: 12,
        fontWeight: "bold",
    }
});

const splitRowsIntoPages = (rows, rowsPerPage) => {
    const pages = [];
    for (let i = 0; i < rows.length; i += rowsPerPage) {
        pages.push(rows.slice(i, i + rowsPerPage));
    }
    return pages;
};

const InvoicePDF = ({ invoiceData, accountData }) => {
    const rowsPerPage = 10;
    const pages = splitRowsIntoPages(invoiceData?.invoicedProducts || [], rowsPerPage);

    const calculateTotalTax = () =>
        (parseFloat(invoiceData?.totalCgst || 0) +
            parseFloat(invoiceData?.totalSgst || 0) +
            parseFloat(invoiceData?.totalIgst || 0)).toFixed(2);

    return (
        <Document>
            {pages.map((pageProducts, pageIndex) => (
                <Page key={pageIndex} size="A4" orientation="landscape" style={styles.page}>
                    {/* Header Section */}
                    <View style={styles.header}>
                        <View>
                            <Text style={styles.headerText}>{accountData?.name?.toUpperCase() || "Company Name"}</Text>
                            <Text style={styles.text}>Email: {accountData?.email || "N/A"}, Mobile: {accountData?.mobile || "N/A"}</Text>
                            <Text style={styles.text}>{accountData?.address || "N/A"}, {accountData?.city || "N/A"}, {accountData?.pincode || "N/A"}, {accountData?.state || "N/A"}</Text>
                        </View>
                        {accountData?.logo && <Image src={accountData.logo} style={styles.image} />}
                    </View>

                    <View style={{ flexDirection: "row", justifyContent: "space-between", marginTop: 0, borderBottom: "1 solid #ddd", paddingBottom: 5 }}>
                        {/* Invoice Information */}
                        <View style={{ flex: 1 }}>
                            <Text style={styles.text}><Text style={{ fontWeight: "bold" }}>Invoice No:</Text> {invoiceData?.code || "N/A"}</Text>
                            <Text style={styles.text}><Text style={{ fontWeight: "bold" }}>Invoice Date:</Text> {invoiceData?.createdAt || "N/A"}</Text>
                            {invoiceData?.gstNumber && (
                                <Text style={styles.text}><Text style={{ fontWeight: "bold" }}>GSTIN:</Text> {invoiceData?.gstNumber}</Text>
                            )}
                            {accountData?.fssaiNumber && (
                                <Text style={styles.text}><Text style={{ fontWeight: "bold" }}>FSSAI No:</Text> {accountData?.fssaiNumber}</Text>
                            )}
                        </View>

                        {/* Customer Information */}
                        <View style={{ flex: 1, alignItems: "flex-end" }}>
                            <Text style={styles.text}><Text style={{ fontWeight: "bold" }}>Customer:</Text> {invoiceData?.customerName || "N/A"}</Text>
                            <Text style={styles.text}><Text style={{ fontWeight: "bold" }}>Phone:</Text> {invoiceData?.customerPhone || "N/A"}</Text>
                        </View>
                    </View>

                    {/* Table Header */}
                    <View style={styles.tableContainer}>
                        <View style={styles.tableHeader}>
                            <Text style={[styles.tableCell, { flex: 2 }]}>Product Name</Text>
                            <Text style={[styles.tableCell, { flex: 1 }]}>HSN Code</Text>
                            <Text style={[styles.tableCell, { flex: 1 }]}>Price</Text>
                            <Text style={[styles.tableCell, { flex: 1 }]}>Discount %</Text>
                            <Text style={[styles.tableCell, { flex: 1 }]}>GST %</Text>
                            <Text style={[styles.tableCell, { flex: 1 }]}>Quantity</Text>
                            <Text style={[styles.tableCell, { flex: 1.5 }]}>Total</Text>
                        </View>

                        {/* Table Rows */}
                        {pageProducts.map((product, index) => (
                            <View style={styles.tableRow} key={index}>
                                <Text style={[styles.tableCell, { flex: 2 }]}>{product.product.name}</Text>
                                <Text style={[styles.tableCell, { flex: 1 }]}>{product.product.hsnCode}</Text>
                                <Text style={[styles.tableCell, { flex: 1 }]}>{product.price}</Text>
                                <Text style={[styles.tableCell, { flex: 1 }]}>{product.discount}</Text>
                                <Text style={[styles.tableCell, { flex: 1 }]}>{product.tax}</Text>
                                <Text style={[styles.tableCell, { flex: 1 }]}>{product.quantity}</Text>
                                <Text style={[styles.tableCell, { flex: 1.5 }]}>{product.totalPrice}</Text>
                            </View>
                        ))}
                    </View>

                    {/* Summary Section */}
                    <View style={styles.summary}>
                        <Text style={styles.summaryText}>Total Items: {invoiceData?.invoicedProducts.length}</Text>
                        <Text style={styles.summaryText}>Total Tax: {calculateTotalTax()}</Text>
                        <Text style={styles.summaryText}>Subtotal: {invoiceData?.subTotal}</Text>
                        <Text style={styles.summaryText}>Grand Total: {invoiceData?.grandTotal}</Text>
                    </View>
                </Page>
            ))}
        </Document>
    );
};

export const InvoiceDownload = ({ invoiceData, accountData, fileName = "invoice" }) => (
    <PDFDownloadLink document={<InvoicePDF invoiceData={invoiceData} accountData={accountData} />} fileName={`${fileName}.pdf`}>
        <button className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600" type="button">Download</button>
    </PDFDownloadLink>
);

export const InvoicePrinter = ({ invoiceData, accountData, isMobile }) => {

    const handlePrint = async () => {
        try {
            // Generate the PDF Blob
            const pdfBlob = await pdf(
                <InvoicePDF
                    invoiceData={invoiceData} accountData={accountData}
                />
            ).toBlob();

            if (isMobile) {
                const blobUrl = URL.createObjectURL(pdfBlob);
                // Wrap in a click event listener
                const link = document.createElement("a");
                link.href = blobUrl;
                link.target = "_blank";
                link.click();

            } else {
                // Create an iframe to render the PDF
                const iframe = document.createElement("iframe");
                iframe.style.display = "none";
                document.body.appendChild(iframe);

                // Load the PDF into the iframe
                iframe.src = URL.createObjectURL(pdfBlob);

                // Listener to clean up the iframe after the print dialog is handled
                const cleanupIframe = () => {
                    document.body.removeChild(iframe); // Remove iframe
                    window.removeEventListener("focus", cleanupIframe); // Remove event listener
                };

                // Attach the listener to the `focus` event
                window.addEventListener("focus", cleanupIframe);

                // Trigger print when iframe loads
                iframe.onload = () => {
                    iframe.contentWindow.print();
                };
            }

        } catch (error) {
            console.error("Error generating PDF for print:", error);
        }
    };


    return <button className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 mr-2" type="button" onClick={handlePrint}>Print</button>;
};